import type { CalendarEvent, CountryCode } from './types';
import { getCountryNameEntry } from './i18n';

/** Resolve a country code to a human-readable country name */
function resolveCountryName(code: string, lang?: string): string {
  const entry = getCountryNameEntry(code as CountryCode, lang || 'en');
  return entry.display || code;
}

/* ── ICS (RFC 5545) ──────────────────────────────────────────────── */

function icsDate(dateStr: string): string {
  // Convert "YYYY-MM-DD" → "YYYYMMDD"
  return dateStr.replace(/-/g, '');
}

function icsEndDate(dateStr: string): string {
  // All-day events in ICS use DTEND = day after last day (exclusive)
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

function escapeIcs(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

export function generateICS(
  events: CalendarEvent[],
  title: string,
  countryCode?: string,
  options?: { lang?: string; translatedTitle?: string },
): string {
  const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const lang = options?.lang;

  const vevents = events.map((e) => {
    const countryName = resolveCountryName(e.country, lang);
    const location = [countryName, e.city].filter(Boolean).join(', ');
    const summary = options?.translatedTitle || e.titleLocal || e.title;
    return [
      'BEGIN:VEVENT',
      `UID:${e.id}@vacant.day`,
      `DTSTAMP:${now}`,
      `DTSTART;VALUE=DATE:${icsDate(e.dateStart)}`,
      `DTEND;VALUE=DATE:${icsEndDate(e.dateEnd)}`,
      `SUMMARY:${escapeIcs(summary)}`,
      e.notes ? `DESCRIPTION:${escapeIcs(e.notes)}` : null,
      location ? `LOCATION:${escapeIcs(location)}` : null,
      'TRANSP:OPAQUE',
      'END:VEVENT',
    ].filter(Boolean).join('\r\n');
  });

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//vacant.day//Calendar Export//EN',
    `X-WR-CALNAME:${escapeIcs(title)}`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...vevents,
    'END:VCALENDAR',
  ].join('\r\n');
}

/* ── CSV ──────────────────────────────────────────────────────────── */

function csvEscape(value: string | undefined | null): string {
  if (!value) return '';
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function generateCSV(events: CalendarEvent[]): string {
  const headers = ['Title', 'Start Date', 'End Date', 'Country', 'City', 'Type', 'Significance', 'Notes'];
  const rows = events.map((e) => [
    csvEscape(e.title),
    e.dateStart,
    e.dateEnd,
    e.country,
    csvEscape(e.city),
    e.type,
    e.significance,
    csvEscape(e.notes),
  ].join(','));

  return [headers.join(','), ...rows].join('\n');
}

/* ── Google Calendar intent URL ──────────────────────────────────── */

export function getGoogleCalendarUrl(
  event: CalendarEvent,
  options?: { lang?: string; translatedTitle?: string },
): string {
  const start = icsDate(event.dateStart);
  const end = icsEndDate(event.dateEnd);
  const title = options?.translatedTitle || event.titleLocal || event.title;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${start}/${end}`,
  });
  if (event.notes) params.set('details', event.notes);
  const countryName = resolveCountryName(event.country, options?.lang);
  const location = [countryName, event.city].filter(Boolean).join(', ');
  if (location) params.set('location', location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/* ── PNG export (calendar screenshot) ────────────────────────────── */

export async function exportCalendarPNG(countryCode?: string): Promise<void> {
  const calendarEl = document.getElementById('calendar');
  if (!calendarEl) return;

  const { toPng } = await import('html-to-image');
  const dataUrl = await toPng(calendarEl, {
    backgroundColor:
      getComputedStyle(document.documentElement)
        .getPropertyValue('--surface-root')
        .trim() || '#ffffff',
    pixelRatio: 2,
  });

  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);

  // Watermark
  const wmSize = Math.round(canvas.width * 0.018);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.font = `600 ${wmSize}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'right';
  ctx.fillText('vacant.day', canvas.width - 24, canvas.height - 16);

  canvas.toBlob((blob) => {
    if (!blob) return;
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `calendar-${countryCode || 'export'}-vacant-day.png`;
    a.click();
    URL.revokeObjectURL(blobUrl);
    window._paq?.push(['trackEvent', 'Export', 'CalendarImage', countryCode || 'unknown']);
  }, 'image/png');
}

/* ── Download helper ─────────────────────────────────────────────── */

export function downloadBlob(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
